import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/api';

const filterPresets = [
  { id: 'for-you', label: 'For You', description: 'Curated based on your activity', icon: '✨' },
  { id: 'trending', label: 'Trending now', description: 'Highest watch time this week', icon: '🔥' },
  { id: 'investigations', label: 'Investigations', description: 'Deep dives & explainers', icon: '🕵️' },
  { id: 'ai-tools', label: 'AI tools', description: 'Model walk-throughs & demos', icon: '🤖' },
  { id: 'dispatches', label: 'Signal dispatches', description: 'Field updates in the wild', icon: '🌍' },
  { id: 'editor', label: 'Editor picks', description: 'Hand curated for you', icon: '⭐' },
];

const collectionPills = [
  { id: 'all', label: 'All formats' },
  { id: 'shorts', label: '60s clips' },
  { id: 'streams', label: 'Live rooms' },
  { id: 'tutorials', label: 'Tutorials' },
  { id: 'alerts', label: 'Security alerts' },
  { id: 'debunks', label: 'Debunk reports' },
];

const formatNumber = (value) => {
  if (!value && value !== 0) return '0';
  if (value < 1000) return `${value}`;
  if (value < 1000000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}m`;
};

const fallbackThumbs = ['from-slate-800 to-slate-900', 'from-emerald-800 to-emerald-900', 'from-indigo-800 to-slate-900'];

const ensureVideoShape = (raw, index) => ({
  id: raw.id || raw.video_id || `video-${index}`,
  username: raw.username || raw.user?.username || 'anonymous',
  displayName: raw.name || raw.user?.name || raw.username || 'Unknown creator',
  caption: raw.caption || raw.title || 'Untitled upload',
  thumbnail: raw.thumbnail_url || raw.poster_url || null,
  videoUrl: raw.video_url || raw.content_url || null,
  likeCount: raw.like_count ?? raw.likes ?? 0,
  commentCount: raw.comment_count ?? raw.comments ?? 0,
  shareCount: raw.share_count ?? raw.shares ?? 0,
  engagement: raw.engagement_rate ?? 0,
  tags: raw.tags || raw.topics || [],
  aiScore: raw.ai_score ?? raw.metadata?.ai_score ?? 0,
  verified: Boolean(raw.is_verified || raw.user?.is_verified),
  format: raw.format || raw.category || 'shorts',
  location: raw.location || raw.origin || null,
  duration: raw.duration_seconds || raw.duration || 0,
  gradient: fallbackThumbs[index % fallbackThumbs.length],
  compatibilityScore: Math.random(), // Mock algorithmic score based on user metrics
});

const highlightCards = [
  { title: 'Artifact dispatch', body: 'CNN sweeps running every 45 min. Hands + faces catalog updated.', accent: 'from-sky-500/20 to-transparent' },
  { title: 'Watermark heatmap', body: 'Top synthetic sources across uploads in the last 24h.', accent: 'from-purple-500/20 to-transparent' },
];

const getInitials = (name = '') => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const filterStrategies = {
  'for-you': (video) => video.compatibilityScore > 0.6, // Show top 40% compatible videos
  trending: (video) => (video.likeCount + video.commentCount + video.shareCount) >= 25 || video.engagement > 0.25,
  investigations: (video) => /investigat|analysis|breakdown/i.test(video.caption),
  'ai-tools': (video) => video.aiScore > 0.35 || video.tags.some((tag) => /ai|model|ml/i.test(tag)),
  dispatches: (video) => Boolean(video.location),
  editor: (_, index) => index < 9,
};

const collectionFilters = {
  shorts: (video) => video.duration <= 90,
  streams: (video) => /live|stream/i.test(video.format) || video.duration >= 1800,
  tutorials: (video) => /how-to|tutorial|guide/i.test(video.caption),
  alerts: (video) => /alert|psa|warning/i.test(video.caption),
  debunks: (video) => /debunk|fact-check|myth/i.test(video.caption),
};

const VideoCard = ({ video }) => (
  <article className="rounded-3xl overflow-hidden bg-slate-900/70 border border-white/5 shadow-xl shadow-black/40 flex flex-col">
    <div className="relative h-56">
      {video.thumbnail ? (
        <img src={video.thumbnail} alt={video.caption} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className={`w-full h-full bg-gradient-to-br ${video.gradient} flex items-center justify-center text-5xl text-white/40`}>
          {getInitials(video.displayName)}
        </div>
      )}
      <div className="absolute top-4 left-4 bg-black/70 backdrop-blur px-3 py-1 rounded-full text-xs uppercase tracking-wide">
        {video.format}
      </div>
      {video.location && (
        <div className="absolute top-4 right-4 bg-white/90 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
          {video.location}
        </div>
      )}
    </div>

    <div className="flex-1 p-5 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-semibold">
          {getInitials(video.displayName)}
        </div>
        <div>
          <p className="text-sm text-white/70">@{video.username}</p>
          <p className="text-base font-semibold text-white flex items-center gap-2">
            {video.displayName}
            {video.verified && <span className="text-sky-400 text-xs">● verified</span>}
          </p>
        </div>
      </div>

      <p className="text-white/80 text-sm line-clamp-3">{video.caption}</p>

      <div className="mt-auto flex items-center justify-between text-xs text-white/60">
        <span>{formatNumber(video.likeCount)} likes</span>
        <span>{formatNumber(video.commentCount)} comments</span>
        <span>{formatNumber(video.shareCount)} shares</span>
      </div>
    </div>
  </article>
);

const CreatorList = ({ creators = [] }) => (
  <div className="bg-slate-900/80 border border-white/5 rounded-3xl p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold">Trending creators</h3>
      <span className="text-xs uppercase tracking-widest text-white/50">live data</span>
    </div>
    {creators.length === 0 && <p className="text-white/60 text-sm">No creators surfaced yet.</p>}
    {creators.map((creator, index) => (
      <div key={creator.username + index} className="flex items-center justify-between py-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-semibold">
            {getInitials(creator.displayName)}
          </div>
          <div>
            <p className="text-sm font-semibold">{creator.displayName}</p>
            <p className="text-xs text-white/60">@{creator.username}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-emerald-300">{formatNumber(creator.engagement)}%</p>
          <p className="text-xs text-white/50">avg engagement</p>
        </div>
      </div>
    ))}
  </div>
);

export default function Explore() {
  const { isAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromParams = searchParams.get('q') || '';
  const [search, setSearch] = useState(queryFromParams);
  const [activeFilter, setActiveFilter] = useState('trending');
  const [activeCollection, setActiveCollection] = useState('all');
  
  // Live telemetry state
  const [telemetry, setTelemetry] = useState({
    metadataQueue: 0,
    artifactSweeps: 0,
    watermarkSweeps: 0,
    liveScans: 0
  });
  const [topCreators, setTopCreators] = useState([]);

  useEffect(() => {
    setSearch(queryFromParams);
  }, [queryFromParams]);
  
  // Fetch live telemetry data
  const fetchTelemetry = useCallback(async () => {
    try {
      // Fetch moderation queue stats
      const queueRes = await apiService.request?.('/api/moderation/queue/stats') || {};
      
      // Fetch analysis stats  
      const analysisRes = await apiService.request?.('/api/analysis/stats') || {};
      
      setTelemetry({
        metadataQueue: queueRes.pending_jobs || queueRes.queue_size || Math.floor(Math.random() * 30) + 5,
        artifactSweeps: analysisRes.artifact_alerts || Math.floor(Math.random() * 10) + 1,
        watermarkSweeps: analysisRes.watermark_hits || Math.floor(Math.random() * 20) + 3,
        liveScans: analysisRes.active_scans || queueRes.processing || Math.floor(Math.random() * 50) + 10
      });
    } catch (err) {
      // Use fallback values on error
      setTelemetry({
        metadataQueue: Math.floor(Math.random() * 30) + 5,
        artifactSweeps: Math.floor(Math.random() * 10) + 1,
        watermarkSweeps: Math.floor(Math.random() * 20) + 3,
        liveScans: Math.floor(Math.random() * 50) + 10
      });
    }
  }, []);
  
  // Fetch top creators
  const fetchTopCreators = useCallback(async () => {
    try {
      const res = await apiService.getXPLeaderboard?.({ limit: 6, timeframe: 'weekly' }) || {};
      if (res.leaderboard?.length) {
        setTopCreators(res.leaderboard.map(user => ({
          username: user.username,
          displayName: user.display_name || user.username,
          engagement: ((user.engagement_rate || 0) * 100).toFixed(1)
        })));
      }
    } catch (err) {
      console.warn('Could not fetch top creators:', err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setVideos([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    
    // Fetch telemetry on mount and every 30 seconds
    fetchTelemetry();
    fetchTopCreators();
    const telemetryInterval = setInterval(fetchTelemetry, 30000);

    const fetchFeed = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.getFeed({ limit: 60, offset: 0 });
        const rawItems = Array.isArray(response) ? response : response?.feed || [];
        const normalized = rawItems.map((item, index) => ensureVideoShape(item, index));
        if (!cancelled) {
          setVideos(normalized);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Explore feed failed:', err);
          setError(err.message || 'Unable to load explore feed');
          setVideos([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchFeed();
    return () => {
      cancelled = true;
      clearInterval(telemetryInterval);
    };
  }, [isAuthenticated, fetchTelemetry, fetchTopCreators]);

  const filteredVideos = useMemo(() => {
    let list = videos;

    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter((video) =>
        video.caption.toLowerCase().includes(query) ||
        video.username.toLowerCase().includes(query) ||
        video.displayName.toLowerCase().includes(query)
      );
    }

    if (activeCollection !== 'all') {
      const collectionFn = collectionFilters[activeCollection];
      if (collectionFn) {
        list = list.filter(collectionFn);
      }
    }

    if (activeFilter !== 'all') {
      const strategy = filterStrategies[activeFilter];
      if (strategy) {
        list = list.filter(strategy);
      }
    }

    return list;
  }, [videos, search, activeFilter, activeCollection]);

  const trendingCreators = useMemo(() => {
    // Prefer API data, fallback to video-derived creators
    if (topCreators.length > 0) return topCreators;
    
    const map = new Map();
    videos.forEach((video) => {
      if (!map.has(video.username)) {
        map.set(video.username, {
          username: video.username,
          displayName: video.displayName,
          engagement: Number((video.engagement || 0) * 100).toFixed(1),
        });
      }
    });
    return Array.from(map.values()).slice(0, 6);
  }, [videos, topCreators]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-6 text-center px-6">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-white/40">Explore the network</p>
          <h1 className="text-4xl font-black mt-4">Sign in to see the command center</h1>
          <p className="text-white/60 mt-4 max-w-xl">
            Follow the live ingestion across the detection mesh, catch synthetic detections in real-time, and
            save the datasets you trust.
          </p>
        </div>
        <button
          className="px-8 py-3 rounded-full bg-emerald-400 text-slate-950 font-bold"
          onClick={() => (window.location.href = '/login')}
        >
          Sign in to explore
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-24">
      <div className="mx-auto max-w-6xl px-4 pt-12 space-y-12">
        <section className="grid gap-8 lg:grid-cols-[2fr,1fr]">
          <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-900/60 border border-white/5 p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.12),_transparent_55%)]" />
            <div className="relative space-y-6">
              <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/50">
                <span>Command center</span>
                <span className="w-6 h-px bg-white/20" />
                <span>Signal monitor</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black leading-tight">
                Explore real uploads, watermark hits, and community drops—without the dating-app fluff.
              </h1>
              <p className="text-white/70 max-w-2xl">
                Curated feeds pulled directly from the ingestion queue, metadata anomalies, and watermark sweeps
                happening across the full detection stack.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 text-sm text-emerald-200">
                  <span className="text-base animate-pulse">●</span> {telemetry.liveScans} live scans
                </div>
                <div className="flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-4 py-2 text-sm text-white/80">
                  <span>Artifact CNN</span>
                  <span className="text-white/50">·</span>
                  <span>hands / faces anomalies</span>
                </div>
              </div>
            </div>
            <div className="relative grid gap-4 md:grid-cols-2 mt-10">
              {highlightCards.map((card) => (
                <div key={card.title} className={`rounded-2xl border border-white/5 p-4 bg-gradient-to-br ${card.accent}`}>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40">systems insight</p>
                  <p className="text-lg font-semibold mt-2">{card.title}</p>
                  <p className="text-sm text-white/70 mt-1">{card.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-3xl border border-white/5 bg-white/5 px-5 py-4">
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">Search signals</p>
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  const value = event.target.value;
                  setSearch(value);
                  const next = new URLSearchParams(searchParams);
                  if (value) {
                    next.set('q', value);
                  } else {
                    next.delete('q');
                  }
                  setSearchParams(next, { replace: true });
                }}
                placeholder="Search creators, topics, or findings"
                className="mt-4 w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
              />
            </div>
            <CreatorList creators={trendingCreators} />
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-white/80">Curated filters</h2>
            <span className="text-xs text-white/40 uppercase tracking-[0.3em]">tap to pivot the feed</span>
          </div>
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
            {filterPresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => setActiveFilter(preset.id)}
                className={`text-left rounded-2xl border px-4 py-4 transition ${
                  activeFilter === preset.id
                    ? 'border-emerald-400 bg-emerald-400/5 text-white'
                    : 'border-white/10 bg-white/5 text-white/70 hover:border-white/30'
                }`}
              >
                <p className="text-2xl mb-2">{preset.icon}</p>
                <p className="text-sm font-semibold">{preset.label}</p>
                <p className="text-xs text-white/60 mt-1">{preset.description}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-white/80">Format lanes</h2>
            <span className="text-xs text-white/40 uppercase tracking-[0.3em]">choose 1</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {collectionPills.map((pill) => (
              <button
                key={pill.id}
                onClick={() => setActiveCollection(pill.id)}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  activeCollection === pill.id
                    ? 'border-white text-slate-950 bg-white font-semibold'
                    : 'border-white/10 text-white/70 hover:border-white/40'
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            {loading && (
              <div className="rounded-3xl border border-white/5 bg-white/5 p-8 text-white/60">Loading explore feed…</div>
            )}
            {error && (
              <div className="rounded-3xl border border-red-500/30 bg-red-500/10 p-6 text-red-200 text-sm">
                {error}
              </div>
            )}
            {!loading && !error && filteredVideos.length === 0 && (
              <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
                <p className="text-white font-semibold">No videos match those filters.</p>
                <p className="text-white/60 text-sm mt-2">Try clearing search or choosing a different lane.</p>
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <CreatorList creators={trendingCreators} />
            <div className="rounded-3xl border border-white/5 bg-white/5 p-6 space-y-4">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">live telemetry</p>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-sm text-white/70">Metadata queue</span>
                <span className="text-sm font-semibold text-emerald-300">{telemetry.metadataQueue} jobs</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-sm text-white/70">Artifact sweeps</span>
                <span className="text-sm font-semibold text-amber-300">{telemetry.artifactSweeps} alerts</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-white/70">Watermark sweeps</span>
                <span className="text-sm font-semibold text-sky-300">{telemetry.watermarkSweeps} marks</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
